/**
 * What the Activity page remembers — `docs/activity.md` §4.6.
 *
 * **Per person, per device, on the server** (Aaron: *"This page should retain
 * its settings by user"*; D6): the tab, the people, the view, Side by side or
 * Combined, Show, the Inbox's filters, the Tasks view, List or Board, the task
 * list open and the All activity view. The browser keeps only the device's id.
 *
 * `activityStateSchema` is the one schema for it: the server validates every
 * PATCH against it and reads every stored row through it, and the client reads
 * its answer the same way. Unknown keys are stripped — a switch revision 1
 * kept simply disappears — and every key is optional, its default filled
 * in by `withDefaults()`.
 */
import { z } from 'zod';
import { filterGroup } from '../list-query.js';
import { CALENDAR_VIEWS } from './week.js';
const uuid = z.string().uuid();
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const ACTIVITY_TABS = ['calendar', 'inbox', 'tasks', 'all'];
export const ACTIVITY_TAB_LABELS = {
    calendar: 'Calendar',
    inbox: 'Inbox',
    tasks: 'Tasks',
    all: 'All activity',
};
export const peopleStateSchema = z.object({
    groupId: uuid.optional(),
    ids: z.array(uuid).max(500),
    /** The branches whose Unassigned is picked. */
    unassigned: z.array(uuid).max(50),
}).strip();
export const showSchema = z.object({
    visits: z.boolean(),
    tasks: z.boolean(),
    blocks: z.boolean(),
    drives: z.boolean(),
}).strip();
/**
 * The phone calendar's four views (Aaron, Oct 25, notes 3 and 7) — Outlook's:
 * **Day** (the default), **3 days**, **Month** and **List** (the agenda). One
 * list in the order the view dropdown offers them, so the dropdown, the
 * calendar and My account's device line cannot disagree.
 */
export const PHONE_CALENDAR_VIEWS = ['day', 'three', 'month', 'list'];
/** What each is called — the dropdown's rows and the device line (§12.5). */
export const PHONE_VIEW_LABELS = {
    day: 'Day',
    three: '3 days',
    month: 'Month',
    list: 'List',
};
/** The phone's view dropdown, in order: `{ value, label }` for each of the four. */
export function phoneCalendarViewOptions() {
    return PHONE_CALENDAR_VIEWS.map((value) => ({ value, label: PHONE_VIEW_LABELS[value] }));
}
/** The patch that switches the phone calendar's view — `change(phoneViewPatch('three'))`. */
export function phoneViewPatch(view) {
    return { calendar: { phoneView: view } };
}
export const calendarStateSchema = z.object({
    viewOne: z.enum(CALENDAR_VIEWS).optional(),
    viewSeveral: z.enum(CALENDAR_VIEWS).optional(),
    several: z.enum(['side', 'combined']).optional(),
    date: day.optional(),
    dateSetOn: day.optional(),
    startHour: z.number().int().min(5).max(10).optional(),
    show: showSchema.partial().strip().optional(),
    panel: z.boolean().optional(),
    /* An old phone's `list` reads as the agenda now (Aaron, Oct 25, note 3.3); no choice at all is Day (note 7). */
    phoneView: z.enum(PHONE_CALENDAR_VIEWS).optional(),
    /**
     * People taken off the side panel's *Calendars* list with its ⋯ (Aaron,
     * Oct 24, note 1.5: "so they aren't permanently part of that list"). Shown
     * again — the people button, *+ Add* — they come back off it.
     */
    hidden: z.array(uuid).max(200).optional(),
}).strip();
export const inboxStateSchema = z.object({
    /* An old 'unassigned' reads as 'all': Unassigned is the people button's now. */
    filter: z.preprocess((value) => (value === 'unassigned' ? 'all' : value), z.enum(['unread', 'all', 'archived'])).optional(),
    channel: z.enum(['all', 'sms', 'email']).optional(),
    conversationId: uuid.optional(),
    /** One of the viewer's own categories narrows the list (Aaron, Oct 24, note 2.2); `null` clears it. */
    categoryId: uuid.nullable().optional(),
}).strip();
/** How long a remembered Board filter may be, as JSON — a few dozen conditions with their names. */
export const BOARD_FILTER_MAX_CHARS = 6000;
export const tasksStateSchema = z.object({
    view: z.enum(['list', 'board']).optional(),
    /* `null` closes the open list: a patch merges per key, so leaving it out would keep it (§8.6). */
    listId: uuid.nullable().optional(),
    viewId: uuid.optional(),
    showDone: z.boolean().optional(),
    /** A task's chat open beside its sheet on this device (Aaron, Oct 25, note 1.6) — a computer or an iPad; a phone opens the task. */
    chat: z.boolean().optional(),
    /**
     * List or Board, per list — a list's id, or `people` for the people
     * button's tasks (Aaron, Oct 23: each remembers the view it was last in).
     * A list rather than a map, newest last, because `jsonb` keeps no key
     * order and the 40 most recently changed are the ones kept.
     */
    views: z.array(z.object({ key: z.string().min(1).max(40), view: z.enum(['list', 'board']) }).strip()).max(40).optional(),
    /**
     * The Board's filter, per list — a list's id, or `people` — as the List's
     * quick filter writes it (Aaron, Oct 26: "filters need to be on the board
     * screen"). The same shape as `views`, newest last, 40 kept; a filter with
     * nothing in it is no filter.
     */
    boardFilters: z.array(z.object({
        key: z.string().min(1).max(40),
        filter: filterGroup.refine((group) => JSON.stringify(group).length <= BOARD_FILTER_MAX_CHARS, 'That filter is too long to remember.'),
    }).strip()).max(40).optional(),
}).strip();
/** The filter a list's Board was last left with on this device, if any (Aaron, Oct 26). */
export function boardFilterOf(choices, key) {
    const found = choices?.find((choice) => choice.key === key)?.filter;
    return found && found.conditions.length > 0 ? found : null;
}
/** What a list was last in on this device, if it has been opened here. */
export function listViewOf(views, key) {
    return views?.find((choice) => choice.key === key)?.view;
}
export const NAV_APPS = ['apple', 'google', 'waze'];
export const activityStateSchema = z.object({
    tab: z.enum(ACTIVITY_TABS).optional(),
    people: peopleStateSchema.optional(),
    recentPeople: z.array(uuid).max(8).optional(),
    calendar: calendarStateSchema.optional(),
    inbox: inboxStateSchema.optional(),
    tasks: tasksStateSchema.optional(),
    all: z.object({ viewId: uuid.optional() }).strip().optional(),
    navApp: z.enum(NAV_APPS).optional(),
}).strip();
/** A PATCH: any subset, merged per top-level key and per `calendar.*` (§4.6). */
export const activityStatePatchSchema = activityStateSchema;
/**
 * Reads a stored state (anything — an old row, a bad one) into a whole one.
 * A `date` set on an earlier day reads as today: somebody who looked at next
 * Tuesday yesterday opens on today this morning.
 */
export function withDefaults(stored, me, today) {
    const parsed = activityStateSchema.safeParse(stored ?? {});
    const value = parsed.success ? parsed.data : {};
    const calendar = value.calendar ?? {};
    const fresh = calendar.dateSetOn === today;
    return {
        tab: value.tab ?? 'calendar',
        people: value.people ?? { ids: [me], unassigned: [] },
        recentPeople: value.recentPeople ?? [],
        calendar: {
            viewOne: calendar.viewOne ?? 'work_week',
            viewSeveral: calendar.viewSeveral ?? 'day',
            several: calendar.several ?? 'side',
            date: fresh && calendar.date ? calendar.date : today,
            dateSetOn: today,
            startHour: calendar.startHour ?? 7,
            show: {
                visits: calendar.show?.visits ?? true,
                tasks: calendar.show?.tasks ?? true,
                blocks: calendar.show?.blocks ?? true,
                drives: calendar.show?.drives ?? true,
            },
            panel: calendar.panel ?? true,
            phoneView: calendar.phoneView ?? 'day',
            hidden: calendar.hidden ?? [],
        },
        inbox: {
            filter: value.inbox?.filter ?? 'all',
            channel: value.inbox?.channel ?? 'all',
            ...(value.inbox?.conversationId ? { conversationId: value.inbox.conversationId } : {}),
            ...(value.inbox?.categoryId ? { categoryId: value.inbox.categoryId } : {}),
        },
        tasks: {
            view: value.tasks?.view ?? 'list',
            ...(value.tasks?.listId ? { listId: value.tasks.listId } : {}),
            ...(value.tasks?.viewId ? { viewId: value.tasks.viewId } : {}),
            showDone: value.tasks?.showDone ?? false,
            chat: value.tasks?.chat ?? false,
            views: value.tasks?.views ?? [],
            boardFilters: value.tasks?.boardFilters ?? [],
        },
        all: value.all?.viewId ? { viewId: value.all.viewId } : {},
        ...(value.navApp ? { navApp: value.navApp } : {}),
    };
}
/** Each list's List-or-Board (Aaron, Oct 23): a changed one moves to the end, and the oldest past 40 go. */
function newestViews(before, change) {
    const changed = new Set(change.map((choice) => choice.key));
    return [...before.filter((choice) => !changed.has(choice.key)), ...change].slice(-40);
}
/**
 * Each list's Board filter: a changed one moves to the end, and the oldest past 40 go. An emptied
 * one stays, as nothing (`boardFilterOf` reads it as no filter): this same merge also gathers the
 * changes waiting to be sent, and a *Clear all* dropped there would never reach the server.
 */
function newestFilters(before, change) {
    const changed = new Set(change.map((choice) => choice.key));
    return [...before.filter((choice) => !changed.has(choice.key)), ...change].slice(-40);
}
/**
 * Merges a PATCH into a stored state: per top-level key, and per key inside
 * `calendar` (and `calendar.show`), so two tabs on one device each keep what
 * they last changed — last write wins per key (§19 B5).
 */
export function mergeState(current, patch) {
    const next = { ...current };
    for (const key of Object.keys(patch)) {
        if (key === 'calendar') {
            const before = current.calendar ?? {};
            const change = patch.calendar ?? {};
            next.calendar = {
                ...before,
                ...change,
                ...(change.show ? { show: { ...(before.show ?? {}), ...change.show } } : {}),
            };
        }
        else if (key === 'tasks') {
            const before = current.tasks ?? {};
            const change = patch.tasks ?? {};
            next.tasks = {
                ...before,
                ...change,
                /* Per list: what changed goes last, and the 40 most recently changed are kept. */
                ...(change.views ? { views: newestViews(before.views ?? [], change.views) } : {}),
                ...(change.boardFilters ? { boardFilters: newestFilters(before.boardFilters ?? [], change.boardFilters) } : {}),
            };
        }
        else if (key === 'inbox' || key === 'all') {
            next[key] = { ...(current[key] ?? {}), ...(patch[key] ?? {}) };
        }
        else {
            next[key] = patch[key];
        }
    }
    /* Somebody shown again is back on the *Calendars* list (note 1.5): showing them is asking for them. */
    const hidden = next.calendar?.hidden;
    if (patch.people && hidden && hidden.length > 0) {
        const shown = new Set(patch.people.ids);
        next.calendar = { ...next.calendar, hidden: hidden.filter((id) => !shown.has(id)) };
    }
    return next;
}
/** The device's kind, decided once by the first page width (§4.6). */
export function deviceKindOf(width, touchFirst) {
    if (width < 768)
        return 'phone';
    if (width < 1180 || touchFirst)
        return 'tablet';
    return 'computer';
}
//# sourceMappingURL=state.js.map